import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";
import Brand from "./Brand";
import LanguageSwitch from "./LanguageSwitch";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { isSuper as isSuperFn, isManager } from "../lib/roles";
import { Archive, CalendarCheck, Users, Layers, CalendarDays, UserX, ScrollText, Settings as SettingsIcon, LogOut, Menu, X, KeyRound } from "lucide-react";

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const isSuper = isSuperFn(user);
  const isMgr = isManager(user);
  const location = useLocation();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (!isMgr) { return undefined; }
    const load = () => { api.get("/admins/pending-count").then((r) => setPending(r.data.count)).catch(() => {}); };
    load();
    window.addEventListener("pending-changed", load);
    return () => { window.removeEventListener("pending-changed", load); };
  }, [isMgr, location.pathname]);
  const links = [
    { to: "/", label: t("nav.attendance"), icon: CalendarCheck, testid: "nav-attendance" },
    { to: "/monthly", label: t("nav.monthly"), icon: CalendarDays, testid: "nav-monthly" },
    { to: "/absentees", label: t("nav.absentees"), icon: UserX, testid: "nav-absentees" },
    ...(isMgr ? [{ to: "/groups", label: t("nav.groups"), icon: Layers, testid: "nav-groups" }] : []),
    ...(isMgr ? [{ to: "/admins", label: t("nav.admins"), icon: Users, testid: "nav-admins" }] : []),
    ...(isMgr ? [{ to: "/new-year", label: t("nav.newYear"), icon: Archive, testid: "nav-new-year" }] : []),
    ...(isSuper ? [{ to: "/audit", label: t("nav.audit"), icon: ScrollText, testid: "nav-audit" }] : []),
    ...(isSuper ? [{ to: "/settings", label: t("nav.settings"), icon: SettingsIcon, testid: "nav-settings" }] : []),
  ];

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const navContent = (
    <>
      <div className="px-5 py-6 border-b border-border">
        <Brand />
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            onClick={() => setOpen(false)}
            data-testid={l.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
            style={({ isActive }) => (isActive ? { backgroundColor: "var(--brand)" } : {})}
          >
            <l.icon className="h-5 w-5 shrink-0" />
            {l.label}
            {l.to === "/admins" && pending > 0 && (
              <span data-testid="pending-count-badge" className="ms-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">{pending}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="px-2 pb-3">
          <div className="text-sm font-medium text-foreground">{user?.name}</div>
          <div className="text-xs text-muted-foreground">{t(`roles.${user?.role}`)}</div>
        </div>
        <button
          onClick={() => { setPwOpen(true); setOpen(false); }}
          data-testid="change-password-btn"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <KeyRound className="h-5 w-5" />
          {t("profile.changePassword")}
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
        >
          <LogOut className="h-5 w-5" />
          {t("nav.logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className={`min-h-screen page-${settings.page_style}`} style={{ backgroundColor: "var(--app-bg)" }}>
      {/* mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card px-4 py-3 no-print">
        <Brand size="sm" />
        <div className="flex items-center gap-2">
          <LanguageSwitch />
          <button onClick={() => setOpen(true)} data-testid="menu-open" className="rounded-lg p-2 hover:bg-secondary">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden no-print">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 start-0 flex w-72 flex-col bg-card shadow-xl">
            <div className="flex justify-end p-2">
              <button onClick={() => setOpen(false)} data-testid="menu-close" className="rounded-lg p-2 hover:bg-secondary">
                <X className="h-5 w-5" />
              </button>
            </div>
            {navContent}
          </aside>
        </div>
      )}

      <div className="md:flex">
        <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 border-e border-border bg-card no-print">
          {navContent}
        </aside>
        <main className="flex-1 md:ms-64">
          <div className="hidden md:flex items-center justify-end gap-3 px-8 pt-6 no-print">
            <LanguageSwitch />
          </div>
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
